import { motion } from "framer-motion";
import { Container, Ship, BadgeCheck } from "lucide-react";
import { useReveal } from "@/components/marketing/Sections";
import { AFRICA, AFRICA_HIGHLIGHT } from "@/components/marketing/africaPaths";
import { WholesaleQuoteButton } from "@/components/marketing/WholesaleQuoteDialog";

const HUB = { x: 601, y: 266 };
const PINS = [
  { id: "ghana", x: 449, y: 315, label: "Ghana", side: -1, dy: 0 },
  { id: "nigeria", x: 478, y: 312, label: "Nigeria", side: 1, dy: -8 },
  { id: "djibouti", x: 573, y: 300, label: "Djibouti", side: -1, dy: -4 },
  { id: "somalia", x: 588, y: 324, label: "Somalia", side: 1, dy: 0 },
  { id: "kenya", x: 561, y: 338, label: "Kenya", side: -1, dy: 4 },
  { id: "botswana", x: 521, y: 404, label: "Botswana", side: 1, dy: 0 },
];

function AfricaMap({ className = "" }) {
  return (
    <svg viewBox="416 248 230 190" className={className} role="img" aria-label="B2B pharmaceutical export from Dubai to Africa" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="af-hl" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4F93E0" /><stop offset="1" stopColor="#245FAF" /></linearGradient>
        <filter id="af-shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#245FAF" floodOpacity="0.35" /></filter>
      </defs>
      <g stroke="#D7E3F2" strokeWidth="0.4" opacity="0.7">
        {[440, 480, 520, 560, 600].map((x) => <line key={x} x1={x} y1="248" x2={x} y2="438" />)}
        {[280, 320, 360, 400].map((y) => <line key={y} x1="416" y1={y} x2="646" y2={y} />)}
      </g>
      <g fill="#C9DBF0" stroke="#fff" strokeWidth="0.5" strokeLinejoin="round">{AFRICA.map((d, i) => <path key={i} d={d} />)}</g>
      <g fill="url(#af-hl)" stroke="#fff" strokeWidth="0.6" strokeLinejoin="round" filter="url(#af-shadow)">{Object.values(AFRICA_HIGHLIGHT).map((d, i) => <path key={i} d={d} />)}</g>
      <g stroke="#F5C64D" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeDasharray="2.5 4">
        {PINS.map((p) => <path key={p.id} d={`M${HUB.x} ${HUB.y} Q ${(HUB.x + p.x) / 2 + 25} ${(HUB.y + p.y) / 2 - 20} ${p.x} ${p.y}`} />)}
      </g>
      {PINS.map((p) => {
        const w = p.label.length * 4.4 + 10;
        const x = p.side < 0 ? -w - 6 : 6;
        return (
          <g key={p.id} transform={`translate(${p.x} ${p.y})`}>
            <circle r="6" fill="#F5C64D" opacity="0.3" />
            <circle r="2.6" fill="#fff" stroke="#0D1B2A" strokeWidth="1.2" />
            <rect x={x} y={p.dy - 6} width={w} height="12" rx="6" fill="#fff" stroke="#D7E3F2" strokeWidth="0.5" />
            <text x={x + w / 2} y={p.dy + 2.3} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="6" fontWeight="600" fill="#0D1B2A">{p.label}</text>
          </g>
        );
      })}
      <g transform={`translate(${HUB.x} ${HUB.y})`}>
        <circle r="7" fill="#F5C64D" opacity="0.3" /><circle r="3.6" fill="#F5C64D" /><circle r="1.4" fill="#0D1B2A" />
      </g>
      <g transform="translate(580 252)">
        <rect width="54" height="12" rx="6" fill="#0D1B2A" />
        <text x="27" y="8.3" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="6" fontWeight="700" fill="#fff">Dubai DMCC hub</text>
      </g>
    </svg>
  );
}

const COUNTRIES = ["Kenya", "Nigeria", "Ghana", "Botswana", "Somalia", "Djibouti"];

export function Africa() {
  const reveal = useReveal();
  return (
    <section id="africa" className="scroll-mt-20 bg-[#F8FAFC] py-20 lg:py-28" data-testid="africa-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <div className="lg:col-span-6">
          <motion.div {...reveal(0)}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#377DD3]">
              <span className="h-px w-6 bg-[#F5C64D]" /> Africa export
            </span>
          </motion.div>
          <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
            B2B export operations across Africa.
          </motion.h2>
          <motion.p {...reveal(0.1)} className="mt-5 text-base leading-relaxed text-[#64748B] sm:text-lg">
            Dubai is a natural gateway to Africa. We run regular B2B export operations to licensed
            importers, wholesalers and hospital groups in Kenya, Nigeria, Ghana, Botswana, Somalia
            and Djibouti — consolidating orders in our DMCC warehouses and shipping by air with
            cold-chain protection to the destination.
          </motion.p>
          <div className="mt-8 space-y-4">
            {[
              { icon: Container, t: "Consolidated export orders", d: "Mixed-product consignments built to each importer's order, with export invoicing and packing lists per shipment." },
              { icon: Ship, t: "Air freight to major gateways", d: "Routed via Nairobi, Lagos, Accra, Gaborone, Mogadishu and Djibouti with validated transit times." },
              { icon: BadgeCheck, t: "Originator-sourced products", d: "Genuine products with batch, expiry and COA details confirmed before every export dispatch." },
            ].map((p, i) => (
              <motion.div key={p.t} {...reveal(0.12 + i * 0.06)} className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#377DD3] to-[#245FAF] text-white shadow-md"><p.icon size={20} /></div>
                <div>
                  <h3 className="font-semibold text-[#0D1B2A]">{p.t}</h3>
                  <p className="mt-1 text-sm text-[#64748B]">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...reveal(0.3)} className="mt-8 flex flex-wrap gap-2" data-testid="africa-country-list">
            {COUNTRIES.map((c) => (
              <span key={c} className="rounded-full border border-[#F5C64D] bg-[#F5C64D]/15 px-3.5 py-1.5 text-xs font-medium text-[#0D1B2A]">{c}</span>
            ))}
          </motion.div>
          <motion.div {...reveal(0.34)} className="mt-8">
            <WholesaleQuoteButton region="Africa" countries={COUNTRIES} testid="africa-quote-button" />
          </motion.div>
        </div>
        <motion.div {...reveal(0.1)} className="lg:col-span-6">
          <div className="relative rounded-3xl border border-[#D7E3F2] bg-white p-6 shadow-[0_30px_60px_-30px_rgba(13,27,42,0.25)]">
            <AfricaMap className="w-full" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
